import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { CancerStage, CLUSTER_TYPES, ClusterType, formatDateBackend, PAGE_TYPES, PageType } from "Backend";
import { contains, distinct } from "helpers/coll";
import { DocumentIntake, Form, FormField, KeyWithValues, LensHitsFilterParamsInput } from "generated/graphql";

export interface LensHitFilterState {
  reviewType: "chrono" | "grouped";
  setReviewType: Dispatch<SetStateAction<"chrono" | "grouped">>;
  selectedPageTypes: PageType[];
  setSelectedPageTypes: Dispatch<SetStateAction<PageType[]>>;
  selectedNotPageTypes: PageType[] | undefined;
  setSelectedNotPageTypes: Dispatch<SetStateAction<PageType[] | undefined>>;
  selectedClusterTypes: ClusterType[];
  setSelectedClusterTypes: Dispatch<SetStateAction<ClusterType[]>>;
  selectedNotClusterTypes: ClusterType[] | undefined;
  setSelectedNotClusterTypes: Dispatch<SetStateAction<ClusterType[] | undefined>>;
  selectedPageLabels: string[];
  setSelectedPageLabels: Dispatch<SetStateAction<string[]>>;
  selectedNotPageLabels: string[] | undefined;
  setSelectedNotPageLabels: Dispatch<SetStateAction<string[] | undefined>>;
  selectedStartDate: Date | undefined;
  setSelectedStartDate: Dispatch<SetStateAction<Date | undefined>>;
  selectedEndDate: Date | undefined;
  setSelectedEndDate: Dispatch<SetStateAction<Date | undefined>>;
  selectedIntakes: DocumentIntake[];
  setSelectedIntakes: Dispatch<SetStateAction<DocumentIntake[]>>;
  selectedCancerStages: CancerStage[];
  setSelectedCancerStages: Dispatch<SetStateAction<CancerStage[]>>;
  showCancerRecurrence: boolean;
  setShowCancerRecurrence: Dispatch<SetStateAction<boolean>>;
  showNegated: boolean | undefined;
  setShowNegated: Dispatch<SetStateAction<boolean | undefined>>;
  hideDuplicates: boolean;
  setHideDuplicates: Dispatch<SetStateAction<boolean>>;
  documentRequiredProperties: KeyWithValues[];
  setDocumentRequiredProperties: Dispatch<SetStateAction<KeyWithValues[]>>;
  documentExcludedProperties: KeyWithValues[];
  setDocumentExcludedProperties: Dispatch<SetStateAction<KeyWithValues[]>>;
  claimantHitsAllowed: boolean;
  familyHitsAllowed: boolean;
  startDateFormatted: string | undefined;
  endDateFormatted: string | undefined;
  selectedIntakeIds: number[];
  selectedRuleIds: string[];
  toggleClaimantAllowed: (checked: boolean) => void;
  toggleFamilyAllowed: (checked: boolean) => void;
  selectRule: (ruleId: string) => void;
  setSelectedRuleIds: Dispatch<SetStateAction<string[]>>;
  selectSection: (sectionKey: string) => void;
  clearFilters: (clearRules?: boolean, clearFieldId?: boolean) => void;
  currentFilters: LensHitsFilterParamsInput;
  currentField: FormField | undefined;
  setFormFieldId: Dispatch<SetStateAction<number>>;
  recommendedOnly: boolean;
  setRecommendedOnly: Dispatch<SetStateAction<boolean>>;
  sortFindings: "alpha" | "default";
  setSortFindings: Dispatch<SetStateAction<"alpha" | "default">>;
}

export const useLensHitFilterState = (
  initialReviewType: "chrono" | "grouped",
  initialSortFindings: "alpha" | "default",
  initialFieldId: number,
  form?: Form
): LensHitFilterState => {
  if (initialFieldId === -1) {
    console.error("Form has no fields or was not loaded before calling useLensHitFilterState!");
  }
  const fields: FormField[] = form?.fields || [];
  const [sortFindings, setSortFindings] = useState<"alpha" | "default">(initialSortFindings);
  const [reviewType, setReviewType] = useState<"chrono" | "grouped">(initialReviewType);
  const [formFieldId, setFormFieldId] = useState<number>(initialFieldId);
  const field = useMemo(() => fields.find((f) => f.id === formFieldId), [formFieldId, fields]);
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);
  const [selectedPageTypes, setSelectedPageTypes] = useState<PageType[]>([]);
  const [selectedNotPageTypes, setSelectedNotPageTypes] = useState<PageType[] | undefined>(undefined);
  const [selectedClusterTypes, setSelectedClusterTypes] = useState<ClusterType[]>([]);
  const [selectedNotClusterTypes, setSelectedNotClusterTypes] = useState<ClusterType[] | undefined>([
    ClusterType.FamilyHistory,
  ]);
  const [selectedPageLabels, setSelectedPageLabels] = useState<string[]>([]);
  const [selectedNotPageLabels, setSelectedNotPageLabels] = useState<string[] | undefined>(undefined);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>(undefined);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(undefined);
  const [selectedIntakes, setSelectedIntakes] = useState<DocumentIntake[]>([]);
  const [documentRequiredProperties, setDocumentRequiredProperties] = useState<KeyWithValues[]>([]);
  const [documentExcludedProperties, setDocumentExcludedProperties] = useState<KeyWithValues[]>([]);
  const [selectedCancerStages, setSelectedCancerStages] = useState<CancerStage[]>([]);
  const [showCancerRecurrence, setShowCancerRecurrence] = useState(false);
  const [showNegated, setShowNegated] = useState<boolean | undefined>(undefined);
  const [hideDuplicates, setHideDuplicates] = useState(true);
  const [recommendedOnly, setRecommendedOnly] = useState(false);

  const claimantHitsAllowed = !contains(selectedClusterTypes, ClusterType.FamilyHistory);
  const familyHitsAllowed = !contains(selectedNotClusterTypes, ClusterType.FamilyHistory);

  const startDateFormatted = formatDateBackend(selectedStartDate);
  const endDateFormatted = formatDateBackend(selectedEndDate);
  const selectedIntakeIds = useMemo(() => selectedIntakes.map((i) => i.id), [selectedIntakes]);

  const toggleClaimantAllowed = (checked: boolean) => {
    if (checked) {
      setSelectedClusterTypes((prev) => prev.filter((c) => c !== "family_history"));
    } else {
      if (familyHitsAllowed) {
        setSelectedClusterTypes((prev) => [...prev, ClusterType.FamilyHistory]);
      }
    }
  };

  const toggleFamilyAllowed = (checked: boolean) => {
    if (checked) {
      setSelectedNotClusterTypes((prev) => (prev || []).filter((c) => c !== ClusterType.FamilyHistory));
    } else {
      if (claimantHitsAllowed) {
        setSelectedNotClusterTypes((prev) => [...(prev || []), ClusterType.FamilyHistory]);
      }
    }
  };

  const selectRule = (ruleId: string) => {
    setSelectedRuleIds((prev) => distinct([ruleId, ...prev]));
  };

  const selectSection = (sectionKey: string) => {
    if (new Array<string>(...CLUSTER_TYPES).indexOf(sectionKey) !== -1) {
      setSelectedClusterTypes((prev) => [...prev, sectionKey as ClusterType]);
    }
    if (new Array<string>(...PAGE_TYPES).indexOf(sectionKey) !== -1) {
      setSelectedPageTypes((prev) => [...prev, sectionKey as PageType]);
    }
  };

  const clearFilters = (clearRules?: boolean) => {
    if (clearRules) {
      setSelectedRuleIds([]);
      setHideDuplicates(true);
    }
    setShowNegated(undefined);
    setSelectedIntakes([]);
    setSelectedClusterTypes([]);
    setSelectedNotClusterTypes([ClusterType.FamilyHistory]);
    setSelectedPageLabels([]);
    setSelectedCancerStages([]);
    setShowCancerRecurrence(false);
    setSelectedPageTypes([]);
    setSelectedStartDate(undefined);
    setSelectedEndDate(undefined);
    setDocumentRequiredProperties([]);
    setDocumentExcludedProperties([]);
  };

  const currentFilters: LensHitsFilterParamsInput = {
    rules: [],
    ruleIds: selectedRuleIds,
    pageTypes: selectedPageTypes,
    notPageTypes: selectedNotPageTypes,
    clusterTypes: selectedClusterTypes,
    notClusterTypes: selectedNotClusterTypes,
    pageLabels: selectedPageLabels,
    notPageLabels: selectedNotPageLabels,
    includeNegated: showNegated,
    dateStart: startDateFormatted,
    dateEnd: endDateFormatted,
    intakeIds: selectedIntakeIds,
    documentIds: [],
    ruleProfileFieldId: formFieldId,
    // Unimplemented in UI State
    cancerStages: selectedCancerStages,
    cancerRecurrence: showCancerRecurrence,
    filterIdentity: hideDuplicates,
    // The user is unable to specify the below filters manually
    pageIdentityGroupId: undefined,
    lensHitIds: undefined,
    pageNumbers: undefined,
    recommendedOnly: recommendedOnly,
    documentRequiredProperties,
    documentExcludedProperties,
  };

  return {
    reviewType,
    setReviewType,
    selectedPageTypes,
    setSelectedPageTypes,
    selectedNotPageTypes,
    setSelectedNotPageTypes,
    selectedClusterTypes,
    setSelectedClusterTypes,
    selectedNotClusterTypes,
    setSelectedNotClusterTypes,
    selectedPageLabels,
    setSelectedPageLabels,
    selectedNotPageLabels,
    setSelectedNotPageLabels,
    selectedStartDate,
    setSelectedStartDate,
    selectedEndDate,
    setSelectedEndDate,
    selectedIntakes,
    setSelectedIntakes,
    selectedCancerStages,
    setSelectedCancerStages,
    showCancerRecurrence,
    setShowCancerRecurrence,
    documentRequiredProperties,
    setDocumentRequiredProperties,
    documentExcludedProperties,
    setDocumentExcludedProperties,
    showNegated,
    setShowNegated,
    hideDuplicates,
    setHideDuplicates,
    claimantHitsAllowed,
    familyHitsAllowed,
    toggleClaimantAllowed,
    toggleFamilyAllowed,
    startDateFormatted,
    endDateFormatted,
    selectedIntakeIds,
    selectedRuleIds,
    selectRule,
    setSelectedRuleIds,
    selectSection,
    clearFilters,
    currentFilters,
    currentField: field,
    setFormFieldId,
    recommendedOnly,
    setRecommendedOnly,
    sortFindings,
    setSortFindings,
  };
};

export const numberOfLensHitFilters = (filters: LensHitsFilterParamsInput): number => {
  return Object.keys(filters).reduce((prev, key) => {
    const filterValue = filters[key];

    // Need to handle some specific filters differently
    if (key === "ruleProfileFieldId") {
      // Skip
    } else if (key === "notClusterTypes") {
      prev += filterValue.filter((f) => f !== "family_history").length;
    } else if (key === "includeNegated" || key === "filterIdentity") {
      // presence of these values means default filter value is being applied
      prev += filterValue === true ? 0 : 1;
    } else if (key === "dateStart" || key === "dateEnd") {
      prev += filterValue !== undefined ? 1 : 0;
    } else if (key === "rules" || key === "ruleIds") {
      // Skip rules altogether
    } else if (typeof filterValue === "boolean" && filterValue) {
      prev += 1;
    } else if (typeof filterValue === "number") {
      prev += 1;
    } else if (filterValue) {
      prev += filterValue.length;
    }
    return prev;
  }, 0);
};
